/**
 * Rule — show view on the shared Inspector grammar (one-grammar law).
 * The DSL is laid out as three Inspector sections (Identity / Trigger &
 * Conditions / Effect) of FieldGrids — phase/skill foreign keys resolved via
 * `RefLink`. The domain-specific Dry-run preview and enable/disable toggle are
 * Header actions; the rule's run-history panel (`RuleRunHistory`) is preserved
 * below the Inspector sections. Ids/timestamps sink to a muted Technical tail;
 * Audit closes the page.
 */
import { useShow, type HttpError } from "@refinedev/core";
import { Ban, Play } from "@/components/ui/icon";

import type { Rule } from "@aos/shared";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";
import { StatusAction } from "../skills/_components/StatusAction";
import { RuleDryRun } from "./_RuleDryRun";
import { RuleRunHistory } from "./_RuleRunHistory";

/**
 * Rule detail view. Shows the full DSL configuration, a Dry-run preview button,
 * an enable/disable toggle (PATCH status), and the rule's run history panel.
 */
export function RuleShow() {
  const { query } = useShow<Rule, HttpError>({ resource: "rules" });
  const { data, isLoading, isError, error, refetch } = query;
  const rule = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !rule) {
    return (
      <ShowPageError
        message={error?.message ?? "Rule not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const reload = () => {
    void refetch();
  };

  return (
    <>
      <Inspector>
        <InspectorHeader
          title={rule.name}
          subtitle="A rule in the engine — its trigger, conditions, effects, and lifecycle."
          status={rule.status}
          actions={
            <>
              <RuleDryRun rule={rule} />
              {rule.status === "disabled" ? (
                <StatusAction
                  resource="rules"
                  id={rule.id}
                  toStatus="active"
                  label="Enable"
                  icon={Play}
                  variant="outline"
                  onDone={reload}
                />
              ) : (
                <StatusAction
                  resource="rules"
                  id={rule.id}
                  toStatus="disabled"
                  label="Disable"
                  icon={Ban}
                  variant="destructive"
                  onDone={reload}
                />
              )}
            </>
          }
        />

        <InspectorSection title="Overview">
          <FieldGrid>
            <Field label="Priority">{rule.priority}</Field>
            <Field label="Effect type">{rule.effect_type}</Field>
            <Field label="Trigger event">{rule.trigger_event_type}</Field>
            <Field label="Context policy">{rule.context_policy}</Field>
            <Field label="Owner">
              <RefLink resource="users" id={rule.owner_user_id} />
            </Field>
            <Field label="Version">{rule.version}</Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Trigger & Conditions">
          <FieldGrid>
            <Field label="Trigger event">{rule.trigger_event_type}</Field>
            <Field label="Artifact type">{rule.artifact_type}</Field>
            <Field label="Required status">{rule.required_status}</Field>
            <Field label="Required phase">
              <RefLink resource="phases" id={rule.required_phase_id} />
            </Field>
            <Field label="Required skill">
              <RefLink resource="skills" id={rule.required_skill_id} />
            </Field>
            <Field label="Required evidence type">
              {rule.required_evidence_type}
            </Field>
            <Field label="Required evidence count">
              {rule.required_evidence_count}
            </Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Effect">
          <FieldGrid>
            <Field label="Effect type">{rule.effect_type}</Field>
            <Field label="Target status">{rule.target_status}</Field>
            <Field label="Target phase">
              <RefLink resource="phases" id={rule.target_phase_id} />
            </Field>
            <Field label="Target skill">
              <RefLink resource="skills" id={rule.target_skill_id} />
            </Field>
            <Field label="Executor type">{rule.executor_type}</Field>
            <Field label="Emitted event">{rule.emitted_event_type}</Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Run History">
          <RuleRunHistory ruleId={rule.id} />
        </InspectorSection>

        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            <Field label="Id">
              <TechnicalId id={rule.id} />
            </Field>
            <Field label="Source path">
              {rule.source_path ? (
                <span className="font-mono text-xs">{rule.source_path}</span>
              ) : null}
            </Field>
            <Field label="Created">{formatDateTime(rule.created_at)}</Field>
            <Field label="Updated">{formatDateTime(rule.updated_at)}</Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Audit">
          <AuditBlock entityType="rules" entityId={rule.id} />
        </InspectorSection>
      </Inspector>
    </>
  );
}

export default RuleShow;
